import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CopyIcon } from "../../components/icons";
import StepIcon from "../../common/StepIcon/StepIcon";
import TypeToConfirmModal from "../../common/TypeToConfirmModal/TypeToConfirmModal";
import Toast from "../../common/Toast/Toast";
import CopyToClipBoard from "../../utils/CopyToClipBoard";
import { BtnVerifyEmailSave } from "../../pages/SettingTemplate/SettingButton";
import DomainConnectForm from "./DomainConnectForm";
import {
  deleteWhiteLabelDomain,
  fetchWhiteLabelConfigStatus,
  isLikelySubdomain,
  retriggerWhiteLabelOnboarding,
  submitWhiteLabelDomain,
  WHITE_LABEL_TERMINAL_STATUSES,
} from "../../service/whiteLabelDomain";

export const POLL_INTERVAL_MS = 20000;

const STATUS_LABEL_KEYS = {
  SUBMITTED: "settings:custom_domain_status_starting",
  CERT_REQUESTED: "settings:custom_domain_status_starting",
  PENDING_DNS_VALIDATION:
    "settings:custom_domain_status_pending_dns_validation",
  CERT_ISSUED: "settings:custom_domain_status_cert_issued",
  DISTRIBUTION_CREATING: "settings:custom_domain_status_setting_up_hosting",
  DISTRIBUTION_DEPLOYED: "settings:custom_domain_status_setting_up_hosting",
  PENDING_FINAL_CUTOVER: "settings:custom_domain_status_pending_final_cutover",
};

const DNS_RECORD_LABEL_KEYS = {
  acm_validation: "settings:custom_domain_dns_record_acm_label",
  final_cutover: "settings:custom_domain_dns_record_cutover_label",
};

const STEPS = ["verify", "hosting", "cutover"];

const STEP_INDEX_BY_STATUS = {
  SUBMITTED: 0,
  CERT_REQUESTED: 0,
  PENDING_DNS_VALIDATION: 0,
  CERT_ISSUED: 1,
  DISTRIBUTION_CREATING: 1,
  DISTRIBUTION_DEPLOYED: 1,
  PENDING_FINAL_CUTOVER: 2,
};

const STEP_TITLE_KEYS = {
  verify: "settings:custom_domain_step_verify_title",
  hosting: "settings:custom_domain_step_hosting_title",
  cutover: "settings:custom_domain_step_cutover_title",
};

const STEP_RECORD_PURPOSES = {
  verify: ["acm_validation"],
  hosting: ["final_cutover"],
  cutover: ["final_cutover"],
};

const REMOVE_CONFIRM_WORD = "DELETE";

const REMOVE_MODAL_COPY = {
  remove: {
    titleKey: "settings:custom_domain_remove_title",
    bodyKey: "settings:custom_domain_remove_body",
  },
  change: {
    titleKey: "settings:custom_domain_change_title",
    bodyKey: "settings:custom_domain_change_body",
  },
};

const CustomDomainSection = ({ onDomainChange }) => {
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(true);
  const [configStatus, setConfigStatus] = useState(null);
  const [statusLoadFailed, setStatusLoadFailed] = useState(false);
  const [domainInput, setDomainInput] = useState("");
  const [domainInputError, setDomainInputError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [removeModalIntent, setRemoveModalIntent] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const isPollingRef = useRef(false);
  const pollIntervalRef = useRef(null);
  const isMountedRef = useRef(true);

  const loadConfigStatus = useCallback(
    async ({ silent = false } = {}) => {
      if (isPollingRef.current) return;
      isPollingRef.current = true;
      if (!silent) setIsLoading(true);
      try {
        const data = await fetchWhiteLabelConfigStatus();
        if (isMountedRef.current) {
          setConfigStatus(data);
          setStatusLoadFailed(false);
        }
      } catch (error) {
        if (isMountedRef.current) setStatusLoadFailed(true);
        if (!silent) {
          Toast({
            type: "error",
            title: t("settings:error"),
            message: t("settings:custom_domain_fetch_error"),
          });
        }
      } finally {
        isPollingRef.current = false;
        if (isMountedRef.current) setIsLoading(false);
      }
    },
    [t],
  );

  const loadConfigStatusRef = useRef(loadConfigStatus);
  loadConfigStatusRef.current = loadConfigStatus;

  useEffect(() => {
    isMountedRef.current = true;
    loadConfigStatusRef.current();
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const onboarding = configStatus?.onboarding || null;
  const onboardingStatus = onboarding?.status || null;
  const isOnboardingInProgress =
    !!onboarding && !WHITE_LABEL_TERMINAL_STATUSES.includes(onboardingStatus);

  useEffect(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (isOnboardingInProgress) {
      pollIntervalRef.current = setInterval(() => {
        loadConfigStatusRef.current({ silent: true });
      }, POLL_INTERVAL_MS);
    }
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [isOnboardingInProgress]);

  useEffect(() => {
    onDomainChange?.(
      configStatus?.custom_domain_configured ? configStatus?.org_domain : null,
    );
  }, [configStatus, onDomainChange]);

  const handleCopy = (value) => {
    CopyToClipBoard(value);
    Toast({
      type: "success",
      title: t("settings:success"),
      message: t("settings:custom_domain_copied"),
    });
  };

  const handleSubmitDomain = async () => {
    const trimmed = domainInput.trim().toLowerCase();
    if (!trimmed) {
      setDomainInputError(t("settings:custom_domain_required"));
      return;
    }
    if (!isLikelySubdomain(trimmed)) {
      setDomainInputError(t("settings:custom_domain_invalid_subdomain"));
      return;
    }
    setDomainInputError("");
    setIsSubmitting(true);
    try {
      await submitWhiteLabelDomain(trimmed);
      Toast({
        type: "success",
        title: t("settings:success"),
        message: t("settings:custom_domain_submit_success"),
      });
      setDomainInput("");
      await loadConfigStatus();
    } catch (error) {
      Toast({
        type: "error",
        title: t("settings:error"),
        message: error?.message || t("settings:custom_domain_submit_error"),
      });
    } finally {
      if (isMountedRef.current) setIsSubmitting(false);
    }
  };

  const handleRetry = async () => {
    setIsRetrying(true);
    try {
      await retriggerWhiteLabelOnboarding();
      Toast({
        type: "success",
        title: t("settings:success"),
        message: t("settings:custom_domain_retry_success"),
      });
      await loadConfigStatus();
    } catch (error) {
      Toast({
        type: "error",
        title: t("settings:error"),
        message: error?.message || t("settings:custom_domain_retry_error"),
      });
    } finally {
      if (isMountedRef.current) setIsRetrying(false);
    }
  };

  const handleRemoveDomain = async () => {
    const domainBeingRemoved = orgDomain || onboarding?.domain;
    setIsDeleting(true);
    try {
      const response = await deleteWhiteLabelDomain();
      Toast({
        type: "success",
        title: t("settings:success"),
        message: response?.data
          ? t("settings:custom_domain_removing_message", {
              domain: domainBeingRemoved,
            })
          : t("settings:custom_domain_remove_success"),
      });
      if (isMountedRef.current) setRemoveModalIntent(null);
      await loadConfigStatus();
    } catch (error) {
      Toast({
        type: "error",
        title: t("settings:error"),
        message: error?.message || t("settings:custom_domain_remove_error"),
      });
    } finally {
      if (isMountedRef.current) setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <div id="custom-domain-section" className="mb-6">
        <header className="text-sm font-medium text-gray-900 mb-2">
          {t("settings:custom_domain_title")}
        </header>
        <div className="h-24 rounded-xl border border-gray-200 bg-gray-50 animate-pulse" />
      </div>
    );
  }

  const customDomainConfigured = configStatus?.custom_domain_configured;
  const orgDomain = configStatus?.org_domain;
  const isBeingRemoved = !!onboarding?.is_being_removed;
  const isRemovalFailed = isBeingRemoved && onboardingStatus === "FAILED";
  const isRemoving = isBeingRemoved && !isRemovalFailed;
  const removeModalCopy =
    REMOVE_MODAL_COPY[removeModalIntent] || REMOVE_MODAL_COPY.remove;

  const renderDnsRecords = (records) => {
    if (!Array.isArray(records) || records.length === 0) return null;
    return (
      <div className="mt-4 space-y-3">
        {records.map((record) => (
          <div
            key={`${record.purpose}-${record.name}`}
            className="rounded-lg border border-gray-200 bg-white p-3"
          >
            <p className="text-xs font-medium text-gray-500 mb-2">
              {t(
                DNS_RECORD_LABEL_KEYS[record.purpose] ||
                  "settings:custom_domain_dns_record_generic_label",
              )}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr_auto] gap-x-4 gap-y-1 text-xs">
              <span className="text-gray-400">
                {t("settings:custom_domain_dns_type")}
              </span>
              <span className="font-mono text-gray-700 break-all">
                {record.type}
              </span>
              <span />

              <span className="text-gray-400">
                {t("settings:custom_domain_dns_name")}
              </span>
              <span className="font-mono text-gray-700 break-all">
                {record.name}
              </span>
              <button
                type="button"
                aria-label={t("settings:custom_domain_copy")}
                title={t("settings:custom_domain_copy")}
                onClick={() => handleCopy(record.name)}
                className="inline-flex items-center justify-center p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 justify-self-start sm:justify-self-auto"
              >
                <CopyIcon size={14} />
              </button>

              <span className="text-gray-400">
                {t("settings:custom_domain_dns_value")}
              </span>
              <span className="font-mono text-gray-700 break-all">
                {record.value}
              </span>
              <button
                type="button"
                aria-label={t("settings:custom_domain_copy")}
                title={t("settings:custom_domain_copy")}
                onClick={() => handleCopy(record.value)}
                className="inline-flex items-center justify-center p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 justify-self-start sm:justify-self-auto"
              >
                <CopyIcon size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div id="custom-domain-section" className="mb-6">
      <header className="text-sm font-medium text-gray-900 mb-1">
        {t("settings:custom_domain_title")}
      </header>
      <p className="text-xs text-gray-500 mb-2">
        {t("settings:custom_domain_helper")}
      </p>

      {isRemoving && (
        <div
          id="custom-domain-removing"
          className="rounded-xl border border-gray-200 bg-white p-4 mb-4"
        >
          <div className="flex items-center gap-2">
            <span
              role="status"
              aria-label={t("settings:custom_domain_step_in_progress")}
              className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 border-red-500 border-t-transparent animate-spin"
            />
            <p className="text-sm font-medium text-gray-900">
              {t("settings:custom_domain_removing_message", {
                domain: onboarding.domain,
              })}
            </p>
          </div>
          <p className="text-xs text-gray-500 mt-1 ml-7">
            {t("settings:custom_domain_removing_hint")}
          </p>
        </div>
      )}

      {customDomainConfigured && orgDomain && !isRemoving && (
        <div
          id="custom-domain-active"
          className="rounded-xl border border-green-200 bg-green-50 p-4 mb-4"
        >
          <p className="text-sm font-medium text-green-800">
            {t("settings:custom_domain_active_message", { domain: orgDomain })}
          </p>
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              id="custom-domain-change"
              onClick={() => setRemoveModalIntent("change")}
              className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300"
            >
              {t("settings:custom_domain_change")}
            </button>
            <button
              type="button"
              id="custom-domain-remove-trigger"
              onClick={() => setRemoveModalIntent("remove")}
              className="rounded-lg border border-red-300 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-200"
            >
              {t("settings:custom_domain_remove")}
            </button>
          </div>
        </div>
      )}

      <TypeToConfirmModal
        isOpen={!!removeModalIntent}
        title={t(removeModalCopy.titleKey)}
        body={t(removeModalCopy.bodyKey, {
          domain: orgDomain,
          word: REMOVE_CONFIRM_WORD,
        })}
        confirmWord={REMOVE_CONFIRM_WORD}
        inputLabel={t("settings:custom_domain_remove_input_label", {
          word: REMOVE_CONFIRM_WORD,
        })}
        cancelLabel={t("settings:cancel")}
        confirmLabel={t("settings:custom_domain_remove_confirm_button")}
        processingLabel={t("settings:custom_domain_removing")}
        isProcessing={isDeleting}
        onCancel={() => setRemoveModalIntent(null)}
        onConfirm={handleRemoveDomain}
        testId="custom-domain-remove"
      />

      {onboarding && onboardingStatus === "FAILED" && !isRemovalFailed && (
        <div
          id="custom-domain-failed"
          className="rounded-xl border border-red-200 bg-red-50 p-4 mb-4"
        >
          <p className="text-sm font-medium text-red-800 mb-1">
            {t("settings:custom_domain_failed_title", {
              domain: onboarding.domain,
            })}
          </p>
          {onboarding.last_error && (
            <p className="text-xs text-red-700 mb-3">{onboarding.last_error}</p>
          )}
          <BtnVerifyEmailSave
            TriggerBtn={handleRetry}
            DisplayText={
              isRetrying
                ? t("settings:custom_domain_retrying")
                : t("settings:custom_domain_retry")
            }
            enabledButton={!isRetrying}
            disabledButton={isRetrying}
          />
        </div>
      )}

      {isRemovalFailed && (
        <div
          id="custom-domain-removal-failed"
          className="rounded-xl border border-red-200 bg-red-50 p-4 mb-4"
        >
          <p className="text-sm font-medium text-red-800 mb-1">
            {t("settings:custom_domain_removal_failed_title", {
              domain: onboarding.domain,
            })}
          </p>
          {onboarding.last_error && (
            <p className="text-xs text-red-700 mb-3">{onboarding.last_error}</p>
          )}
          <BtnVerifyEmailSave
            TriggerBtn={handleRetry}
            DisplayText={
              isRetrying
                ? t("settings:custom_domain_retrying")
                : t("settings:custom_domain_retry")
            }
            enabledButton={!isRetrying}
            disabledButton={isRetrying}
          />
        </div>
      )}

      {isOnboardingInProgress && !isRemoving && (
        <div
          id="custom-domain-in-progress"
          className="rounded-xl border border-gray-200 bg-white p-4 mb-4"
        >
          <p className="text-sm font-medium text-gray-900 mb-4">
            {customDomainConfigured
              ? t("settings:custom_domain_setting_up", {
                  domain: onboarding.domain,
                })
              : onboarding.domain}
          </p>
          <ol>
            {STEPS.map((stepKey, index) => {
              const currentStepIndex =
                STEP_INDEX_BY_STATUS[onboardingStatus] ?? 0;
              const state =
                index < currentStepIndex
                  ? "complete"
                  : index === currentStepIndex
                    ? "current"
                    : "upcoming";
              const isLast = index === STEPS.length - 1;
              const records = (onboarding.dns_records || []).filter((r) =>
                STEP_RECORD_PURPOSES[stepKey].includes(r.purpose),
              );

              return (
                <li key={stepKey} className="relative pl-8 pb-6 last:pb-0">
                  {!isLast && (
                    <span
                      aria-hidden
                      className={`absolute left-[9px] top-5 bottom-0 w-px ${
                        state === "complete"
                          ? "bg-cfPrimaryButton/40"
                          : "bg-gray-200"
                      }`}
                    />
                  )}
                  <span className="absolute left-0 top-0.5">
                    <StepIcon
                      state={state}
                      inProgressLabel={t(
                        "settings:custom_domain_step_in_progress",
                      )}
                    />
                  </span>
                  <p
                    className={`text-sm font-medium ${
                      state === "upcoming" ? "text-gray-400" : "text-gray-900"
                    }`}
                  >
                    {t(STEP_TITLE_KEYS[stepKey])}
                  </p>
                  {state === "current" && (
                    <>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {t(
                          STATUS_LABEL_KEYS[onboardingStatus] ||
                            STATUS_LABEL_KEYS.SUBMITTED,
                        )}
                      </p>
                      {renderDnsRecords(records)}
                    </>
                  )}
                  {state === "complete" && (
                    <p className="text-xs text-cfPrimaryButton mt-0.5">
                      {t("settings:custom_domain_step_complete")}
                    </p>
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      )}

      {!customDomainConfigured && !onboarding && statusLoadFailed && (
        <div
          id="custom-domain-load-error"
          className="rounded-xl border border-red-200 bg-red-50 p-4"
        >
          <p className="text-sm font-medium text-red-800 mb-3">
            {t("settings:custom_domain_fetch_error")}
          </p>
          <BtnVerifyEmailSave
            TriggerBtn={() => loadConfigStatus()}
            DisplayText={t("settings:custom_domain_retry")}
            enabledButton={!isLoading}
            disabledButton={isLoading}
          />
        </div>
      )}

      {!customDomainConfigured && !onboarding && !statusLoadFailed && (
        <DomainConnectForm
          idPrefix="custom-domain-empty"
          value={domainInput}
          error={domainInputError}
          isSubmitting={isSubmitting}
          onChange={(e) => {
            setDomainInput(e.target.value);
            if (domainInputError) setDomainInputError("");
          }}
          onSubmit={handleSubmitDomain}
        />
      )}
    </div>
  );
};

export default CustomDomainSection;
